import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Button, 
  Alert,
  CircularProgress 
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import SEO from '../components/SEO';
import SEOErrorBoundary from '../components/SEOErrorBoundary';
import useStructuredData from '../hooks/useStructuredData';
import { useLanguageUrl } from '../utils/languageUtils';

const Unsubscribe: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { t, i18n } = useTranslation();
  const { getWebPageData } = useStructuredData();
  const getLanguageUrl = useLanguageUrl();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const pageTitle = i18n.language === 'en' 
    ? 'Unsubscribe from Legal Alerts' 
    : 'Cancelar Suscripción de Alertas Legales';
  const pageDescription = i18n.language === 'en'
    ? 'Unsubscribe from legal publication alerts and notifications from AlertaLegal.'
    : 'Cancela tu suscripción de alertas y notificaciones de publicaciones legales de AlertaLegal.';

  const structuredData = getWebPageData(pageTitle, pageDescription, '/unsubscribe');

  useEffect(() => {
    const processUnsubscribe = async () => {
      if (!token) {
        setStatus('error');
        setErrorMessage(t('unsubscribe.invalidToken'));
        return;
      }

      try {
        await api.unsubscribeUser(token);
        setStatus('success');
      } catch (error: any) {
        setStatus('error');
        setErrorMessage(error.message || t('unsubscribe.error'));
      }
    };

    processUnsubscribe();
  }, [token, t]);

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            <CircularProgress />
            <Typography variant="body1" color="text.secondary">
              {t('unsubscribe.processing')}
            </Typography>
          </Box>
        );

      case 'success':
        return (
          <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
            <Alert severity="success" sx={{ width: '100%' }}>
              <Typography variant="h6" gutterBottom>
                {t('unsubscribe.successTitle')}
              </Typography>
              <Typography variant="body2">
                {t('unsubscribe.successMessage')}
              </Typography>
            </Alert>
            
            <Box display="flex" gap={2} flexWrap="wrap" justifyContent="center">
              <Button 
                component={Link} 
                to={getLanguageUrl('register')}
                variant="outlined" 
                color="primary"
              >
                {t('unsubscribe.resubscribe')}
              </Button>
              <Button 
                component={Link} 
                to={getLanguageUrl('home')}
                variant="contained" 
                color="primary"
              >
                {t('unsubscribe.backToHome')}
              </Button>
            </Box>
          </Box>
        );

      case 'error':
        return (
          <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
            <Alert severity="error" sx={{ width: '100%' }}>
              <Typography variant="h6" gutterBottom>
                {t('unsubscribe.errorTitle')}
              </Typography>
              <Typography variant="body2">
                {errorMessage}
              </Typography>
            </Alert>
            
            <Button 
              component={Link} 
              to={getLanguageUrl('home')}
              variant="contained" 
              color="primary"
            >
              {t('unsubscribe.backToHome')}
            </Button>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <SEOErrorBoundary fallbackTitle="Unsubscribe from Legal Alerts - AlertaLegal" fallbackDescription="Unsubscribe from legal publication alerts and notifications from AlertaLegal.">
        <SEO 
          title={pageTitle}
          description={pageDescription}
          structuredData={structuredData}
          hreflangEnabled={true}
        />
      </SEOErrorBoundary>
      <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          borderRadius: 3,
          textAlign: 'center'
        }}
      >
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom 
          color="primary.main"
          fontWeight="bold"
        >
          🛡️ {t('unsubscribe.title')}
        </Typography>
        
        <Typography 
          variant="subtitle1" 
          color="text.secondary" 
          gutterBottom
          sx={{ mb: 3 }}
        >
          {t('unsubscribe.subtitle')}
        </Typography>

        {renderContent()}
      </Paper>
    </Container>
    </>
  );
};

export default Unsubscribe;